/**
 * ProjectionModal Component
 * Modal for creating and editing box office projections with form validation
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Divider
} from '@mui/material';
import {
  Close,
  Save,
  Edit,
  Add
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useProjectionForm } from '../../hooks/useProjectionForm';
import ProjectionForm from './ProjectionForm';

const ProjectionModal = ({ 
  open, 
  onClose, 
  projection = null, 
  filmId, 
  filmTitle,
  onSuccess,
  canManage = false
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  
  // Use authentication context
  const { isSuperAdmin } = useAuth();
  const manageAllowed = canManage || isSuperAdmin();
  
  // Determine if this is edit mode
  const isEditMode = !!projection;
  
  // Initialize form data - memoized to prevent unnecessary re-renders
  const initialData = useMemo(() => {
    if (isEditMode && projection) {
      return {
        filmId: projection.filmId,
        projectionDate: projection.projectionDate?.split('T')[0] || '',
        outlierMinimum: projection.outlierMinimum?.toString() || '',
        actualProjection: projection.actualProjection?.toString() || '',
        outlierCeiling: projection.outlierCeiling?.toString() || '',
        notes: projection.notes || ''
      };
    }
    return {
      filmId: filmId || '',
      projectionDate: new Date().toISOString().split('T')[0],
      outlierMinimum: '',
      actualProjection: '',
      outlierCeiling: '',
      notes: ''
    };
  }, [isEditMode, projection, filmId]);

  // Use projection form hook
  const {
    formData,
    isValid,
    isDirty,
    errors,
    touched,
    updateField,
    validateForm,
    getSubmissionData,
    hasFieldError,
    getFieldError,
    resetForm
  } = useProjectionForm(initialData, isEditMode);

  // Reset form and clear errors when modal opens
  useEffect(() => {
    if (open) {
      setSubmitError(null);
      // Reset form to initial data when modal opens
      // Only reset if we're switching between edit/create modes or opening fresh
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Handle form submission
  const handleSubmit = async () => {
    if (!manageAllowed) {
      setSubmitError('You do not have permission to perform this action');
      return;
    }

    // Validate form
    const validation = validateForm();
    if (!validation.isValid) {
      // Show validation errors to user
      const errorMessages = Object.entries(validation.errors).map(([field, error]) => 
        `${field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}: ${error}`
      ).join('; ');
      setSubmitError(`Please fix the following errors: ${errorMessages}`);
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      const submissionData = getSubmissionData();
      
      // Note: Not adding createdBy field as API doesn't expect it
      // The API will handle user attribution on the backend

      console.log('🚀🚀🚀 SUBMITTING PROJECTION DATA 🚀🚀🚀');
      console.log('📤 Raw submission data:', JSON.stringify(submissionData, null, 2));
      console.log('📊 Data types:', {
        filmId: typeof submissionData.filmId,
        projectionDate: typeof submissionData.projectionDate,
        outlierMinimum: typeof submissionData.outlierMinimum,
        actualProjection: typeof submissionData.actualProjection,
        outlierCeiling: typeof submissionData.outlierCeiling,
        notes: typeof submissionData.notes,
        createdBy: typeof submissionData.createdBy
      });
      console.log('🎯 Expected format should match:', {
        filmId: "tt30144839",
        projectionDate: "2025-09-19",
        outlierMinimum: 21000000,
        actualProjection: 24000000,
        outlierCeiling: 32000000,
        notes: "Final Box office projection"
      });

      // Import API service
      const { default: apiService } = await import('../../lib/services/apiService');
      
      let response;
      if (isEditMode) {
        // Update existing projection
        response = await apiService.updateBoxOfficeProjection(projection.id, submissionData);
      } else {
        // Create new projection
        response = await apiService.createBoxOfficeProjection(submissionData);
      }
      
      console.log(`${isEditMode ? 'Updated' : 'Created'} projection successfully:`, response);
      
      // Call success callback
      if (onSuccess) {
        onSuccess(response.data || submissionData);
      }
      
      // Close modal
      onClose();
      
    } catch (error) {
      console.error('Failed to submit projection:', error);
      setSubmitError(error.message || 'Failed to save projection');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (submitting) return;
    
    // Check if form has unsaved changes
    if (isDirty) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to close?'
      );
      if (!confirmed) return;
    }
    
    onClose();
  };

  // Handle form field changes
  const handleFieldChange = (field, value) => {
    updateField(field, value);
    // Clear submit error when user makes changes
    if (submitError) {
      setSubmitError(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={submitting}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isEditMode ? (
              <>
                <Edit sx={{ color: 'primary.main' }} />
                <Typography variant="h6">
                  Edit Box Office Projection
                </Typography>
              </>
            ) : (
              <>
                <Add sx={{ color: 'primary.main' }} />
                <Typography variant="h6">
                  Create Box Office Projection
                </Typography>
              </>
            )}
          </Box>
          <IconButton
            onClick={handleClose}
            disabled={submitting}
            size="small"
          >
            <Close />
          </IconButton>
        </Box>
        
        {filmTitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            For: <strong>{filmTitle}</strong>
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ pb: 1 }}>
        {/* Submit Error Alert */}
        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        )}

        {!manageAllowed && !submitError && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Only super admins can create or edit box office projections.
          </Alert>
        )}

        {/* Form Validation Errors */}
        {Object.keys(errors).length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Please fix the following errors:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {Object.entries(errors).map(([field, error]) => (
                <li key={field}>
                  <Typography variant="body2">
                    <strong>{field}:</strong> {error}
                  </Typography>
                </li>
              ))}
            </ul>
          </Alert>
        )}

        {/* Projection Form */}
        <ProjectionForm
          formData={formData}
          errors={errors}
          touched={touched}
          onFieldChange={handleFieldChange}
          hasFieldError={hasFieldError}
          getFieldError={getFieldError}
          isEditMode={isEditMode}
          disabled={submitting || !manageAllowed}
        />

        {/* Form Status */}
        <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Form Status
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Box sx={{ 
              px: 1, 
              py: 0.5, 
              borderRadius: 1, 
              backgroundColor: isValid ? 'success.light' : 'info.light',
              color: isValid ? 'success.dark' : 'info.dark'
            }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {isValid ? '✓ Valid' : '○ Ready to Submit'}
              </Typography>
            </Box>
            {isDirty && (
              <Box sx={{ 
                px: 1, 
                py: 0.5, 
                borderRadius: 1, 
                backgroundColor: 'warning.light',
                color: 'warning.dark'
              }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  ● Unsaved Changes
                </Typography>
              </Box>
            )}
            {isEditMode && (
              <Box sx={{ 
                px: 1, 
                py: 0.5, 
                borderRadius: 1, 
                backgroundColor: 'info.light',
                color: 'info.dark'
              }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  ✏️ Edit Mode
                </Typography>
              </Box>
            )}
          </Box>
          
          {/* Validation errors will only show after submission attempt */}
          {submitError && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="error" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                Submission Error:
              </Typography>
              <Typography variant="caption" color="error">
                {submitError}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={submitting}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={submitting || !manageAllowed}
          variant="contained"
          startIcon={submitting ? <CircularProgress size={16} /> : <Save />}
        >
          {submitting 
            ? (isEditMode ? 'Updating...' : 'Creating...') 
            : (isEditMode ? 'Update Projection' : 'Create Projection')
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectionModal;
